import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useContext, useState } from "react";
import { SDKContext } from "../context/sdk";
import DesignActionButton from "./DesignActionButton";

type AutoDesignerConfigNames = "max_fit" | "energy_target";

interface Inverter {
  name: string;
  id: string;
}

interface AutoStringerConfig {
  components: {
    dcOptimizers: string[];
    microInverters: string[];
    stringInverters: string[];
  };
}

const DEFAULT_AUTOSTRINGER_CONFIG: AutoStringerConfig = {
  components: {
    dcOptimizers: [],
    microInverters: [],
    stringInverters: [],
  }
};

/**
 * This component houses the autodesign/autoroof buttons
 * and can be copied/pasted to serve as the basis for any other button-holding component.
 */
export default function AutoJobButtonsPanel() {
  const { components } = useContext(SDKContext);
  const [autoDesignerConfig, setAutoDesignerConfig] = useState<AutoDesignerConfigNames>("max_fit");
  const [autoStringerConfig, setAutoStringerConfig] = useState<AutoStringerConfig>(DEFAULT_AUTOSTRINGER_CONFIG);
  const {
    solarPanels,
    solarInverters,
  } = components || {};

  const configs = {
    energy_target: {
      components: {
        solarPanels: solarPanels?.length ? [solarPanels[0].id] : undefined,
        microInverters: solarInverters ? [solarInverters[0].id] : undefined,
      },
      mode: "energy",
      target: 7500,
      placementOptions: {
        columnSpacingMeters: 0.01,
        rowSpacingMeters: 0.01,
        orientations: ["portrait"],
      },
    },
    max_fit: {
      components: {
        solarPanels: [components?.solarPanels[0].id],
      },
      mode: "max_fit",
      placementOptions: {
        columnSpacingMeters: 0.01,
        rowSpacingMeters: 0.01,
        minSap: 0.5,
        minTsrf: 0.5,
        orientations: ["portrait"],
      },
    },
  };

  const handleAutoDesignerChange = (
    _event: React.MouseEvent<HTMLElement>,
    newConfig: AutoDesignerConfigNames
  ) => setAutoDesignerConfig(newConfig);

  const handleAutoStringerInverterChange = (event: SelectChangeEvent<string>) => {
    const inverterId = event.target.value;
    setAutoStringerConfig({
      components: {
        ...DEFAULT_AUTOSTRINGER_CONFIG.components,
        stringInverters: [inverterId],
      }
    });
  }

  return (
    <div style={{ padding: 0 }}>
      <div style={{ flexDirection: 'column', padding: 0, backgroundColor: 'rgba(255,255,255,0.5)' }}>
        <DesignActionButton onClick={(design) => design.runAutoRoof()}>
          AutoRoof
        </DesignActionButton>
        <DesignActionButton
          onClick={(design) =>
            design.runAutoDesigner(configs[autoDesignerConfig])
          }
        >
          AutoDesigner
        </DesignActionButton>
        <DesignActionButton onClick={(design) => design.runAutoStringer(autoStringerConfig)}>
          AutoStringer
        </DesignActionButton>
        <div style={{ maxWidth: '300px', margin: '1em auto 0 auto' }}>
          <p>AutoDesigner Configuration</p>
          <ToggleButtonGroup
            aria-label="auto designer configuration"
            exclusive
            onChange={handleAutoDesignerChange}
            size="small"
            value={autoDesignerConfig}
          >
            <ToggleButton sx={{ backgroundColor: 'white' }} color="success" value="max_fit">
              Max Fit
            </ToggleButton>
            <ToggleButton sx={{ backgroundColor: 'white' }} color="success" value="energy_target">
              Energy Target
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div>
          <p>AutoStringer Configuration</p>
          <FormControl style={{ margin: '1rem', width: '440px' }}>
            <InputLabel id="autostringer-panel-select-label">AutoStringer Inverter</InputLabel>
            <Select
              disabled={!solarInverters?.length}
              labelId="autostringer-panel-select-label"
              id="autostringer-panel-select"
              value={autoStringerConfig.components.stringInverters[0] || ''}
              label="AutoStringer Inverter"
              onChange={handleAutoStringerInverterChange}
            >
              {(solarInverters || [])
                .map((inverter: Inverter) => {
                  return <MenuItem value={inverter.id} key={inverter.id}>{inverter.name}</MenuItem>;
                })}
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  );
}
